/**
 * On-screen pop-up for high-signal deal sign-off notifications (#3801, D5-ASW-001).
 *
 * The bell alone was being missed: a paralegal did not notice a deal had been
 * signed off or sent back. When a NEW notification of one of the
 * {@link TOAST_NOTIFICATION_TYPES} arrives for the signed-in user, show a toast
 * with its title, body and an "Open" link, in addition to the bell.
 *
 * No new infrastructure: it rides the bell's existing unread-count poll. Only
 * when the count first loads non-zero, or rises, does it fetch the unread feed
 * (the same self-scoped endpoint the bell's dropdown uses) and look for
 * qualifying items.
 *
 * Once per notification: every id that has been toasted is remembered in
 * localStorage, so a page reload (or a later poll) never re-toasts it. Only
 * notifications from the last {@link RECENT_WINDOW_MS} qualify, so a backlog of
 * old unread items never floods the screen, and at most
 * {@link MAX_TOASTS_PER_CHECK} are shown per check (the rest stay on the bell).
 */
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  type AppNotification,
  type NotificationType,
  listNotifications,
  markNotificationRead,
} from "../../api/notifications";
import { useToast } from "../ui/useToast";
import { useIsViewingAs } from "../../contexts/viewAs";

/** The only notification types that pop up -- never task reminders etc. */
export const TOAST_NOTIFICATION_TYPES: ReadonlySet<NotificationType> = new Set<NotificationType>([
  "deal_ready_for_paralegal",
  "signoff_decision",
  "deal_sent_back",
]);

export const TOASTED_STORAGE_KEY = "crm.notifications.toasted";
/** Bound the remembered-id list so localStorage never grows without limit. */
const MAX_REMEMBERED = 200;
/** Only notifications this recent pop up (older unread ones stay on the bell). */
export const RECENT_WINDOW_MS = 24 * 60 * 60 * 1000;
export const MAX_TOASTS_PER_CHECK = 3;
/** Long enough to read a manager's send-back reason. */
const TOAST_DURATION_MS = 10_000;

function readToasted(): number[] {
  try {
    const raw = window.localStorage.getItem(TOASTED_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((v): v is number => typeof v === "number") : [];
  } catch {
    return [];
  }
}

function writeToasted(ids: number[]) {
  try {
    window.localStorage.setItem(
      TOASTED_STORAGE_KEY,
      JSON.stringify(ids.slice(-MAX_REMEMBERED)),
    );
  } catch {
    // Storage unavailable (private mode / quota): worst case a reload re-toasts.
  }
}

/** The unread items that should pop up now, newest first. */
export function selectToastable(
  items: AppNotification[],
  toasted: ReadonlySet<number>,
  now: number = Date.now(),
): AppNotification[] {
  return items.filter((n) => {
    if (n.is_read || toasted.has(n.id)) return false;
    if (!TOAST_NOTIFICATION_TYPES.has(n.notification_type)) return false;
    const created = new Date(n.created_at).getTime();
    return !Number.isNaN(created) && now - created <= RECENT_WINDOW_MS;
  });
}

/**
 * Pop up new sign-off notifications. ``unread`` is the bell's polled unread
 * count; ``onOpened`` lets the bell drop its badge when a toast's link is used.
 */
export function useDecisionToasts(unread: number, onOpened?: () => void) {
  const toast = useToast();
  const navigate = useNavigate();
  const lastUnread = useRef(0);
  // D2-SEC-011 (#3962): no pop-ups while viewing as an agent -- opening one
  // marks it read (a refused write), and they are the agent's, not the viewer's.
  const viewingAs = useIsViewingAs();
  // Keep the latest callbacks without re-running the effect on every render.
  const deps = useRef({ toast, navigate, onOpened });
  useEffect(() => {
    deps.current = { toast, navigate, onOpened };
  });

  useEffect(() => {
    const previous = lastUnread.current;
    lastUnread.current = unread;
    // Only look when there is something unread that was not there before.
    if (viewingAs || unread <= 0 || unread <= previous) return;

    let cancelled = false;
    listNotifications({ unread: true })
      .then((items) => {
        if (cancelled) return;
        const remembered = readToasted();
        const fresh = selectToastable(items, new Set(remembered));
        if (fresh.length === 0) return;
        // Remember every qualifying id -- including any beyond the per-check
        // cap -- so none of them pops up later or on reload.
        writeToasted([...remembered, ...fresh.map((n) => n.id)]);
        const { toast: api } = deps.current;
        for (const n of fresh.slice(0, MAX_TOASTS_PER_CHECK)) {
          api.show({
            variant: "info",
            title: n.title,
            message: n.body || n.title,
            duration: TOAST_DURATION_MS,
            action: n.link
              ? {
                  label: "Open",
                  onClick: () => {
                    markNotificationRead(n.id)
                      .then(() => deps.current.onOpened?.())
                      .catch(() => {});
                    deps.current.navigate(n.link);
                  },
                }
              : undefined,
          });
        }
      })
      .catch(() => {
        // Silent: a pop-up is additive, the bell still carries the item.
      });
    return () => {
      cancelled = true;
    };
  }, [unread, viewingAs]);
}
